class PremiumVideoStory extends HTMLElement {
  connectedCallback() {
    this.classList.add('is-reveal-ready');
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.track = this.querySelector('[data-pvs-trust]');
    this.slides = Array.from(this.querySelectorAll('[data-pvs-feature]'));
    this.dots = Array.from(this.querySelectorAll('[data-pvs-dot]'));
    this.index = 0;
    this.interval = Number(this.dataset.trustInterval) || 2000;
    this.videoAutoplay = this.dataset.videoAutoplay === 'true';

    this.slides.forEach((slide, index) => slide.style.setProperty('--pvs-feature-index', index));
    this.track?.addEventListener('pointerdown', this.onPointerDown);
    this.track?.addEventListener('pointerup', this.onPointerUp);
    this.track?.addEventListener('pointercancel', this.onPointerUp);
    this.track?.addEventListener('scroll', this.onScroll, { passive: true });
    this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));
    this.mobileQuery.addEventListener('change', this.onViewportChange);
    this.reducedMotionQuery.addEventListener('change', this.onViewportChange);
    this.addEventListener('click', this.onVideoPlayClick, true);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    this.setupObserver();
    this.updateDots();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('pointerdown', this.onPointerDown);
    this.track?.removeEventListener('pointerup', this.onPointerUp);
    this.track?.removeEventListener('pointercancel', this.onPointerUp);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.dots.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
    this.mobileQuery?.removeEventListener('change', this.onViewportChange);
    this.reducedMotionQuery?.removeEventListener('change', this.onViewportChange);
    this.removeEventListener('click', this.onVideoPlayClick, true);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.observer?.disconnect();
    this.stopAutoplay();
    this.pauseAutoplayVideos();
    clearTimeout(this.restartTimer);
    clearTimeout(this.scrollTimer);
  }

  onPointerDown = () => {
    clearTimeout(this.restartTimer);
    this.stopAutoplay();
  };

  onPointerUp = () => {
    this.updateIndex();
    this.restartAutoplay();
  };

  onScroll = () => {
    clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(() => this.updateIndex(), 100);
  };

  onDotClick = (event) => {
    const dot = event.currentTarget;
    this.goToSlide(Number(dot.dataset.pvsDot));
    this.restartAutoplay();
  };

  onViewportChange = () => {
    this.goToSlide(0, 'auto');
    this.startAutoplay();
    this.pauseAutoplayVideos();
    this.startVideoAutoplay();
  };

  onVisibilityChange = () => {
    if (document.hidden) {
      this.stopAutoplay();
      this.pauseAutoplayVideos();
    } else {
      this.startAutoplay();
      this.startVideoAutoplay();
    }
  };

  onVideoPlayClick = (event) => {
    const button = event.target.closest?.('.pvs__video .deferred-media__poster-button');
    if (!button || !this.contains(button)) return;

    button.classList.remove('is-clicked');
    void button.offsetWidth;
    button.classList.add('is-clicked');
    setTimeout(() => button.classList.remove('is-clicked'), 720);
  };

  onBlockSelect = (event) => {
    const slide = event.target.closest?.('[data-pvs-feature]');
    if (!slide || !this.mobileQuery.matches) return;
    this.goToSlide(this.slides.indexOf(slide));
    this.restartAutoplay();
  };

  startAutoplay() {
    this.stopAutoplay();
    if (
      !this.track ||
      !this.isInView ||
      document.hidden ||
      !this.mobileQuery.matches ||
      this.reducedMotionQuery.matches ||
      this.slides.length < 2
    ) return;
    this.timer = setInterval(() => this.goToSlide((this.index + 1) % this.slides.length), this.interval);
  }

  stopAutoplay() {
    clearInterval(this.timer);
    this.timer = undefined;
  }

  restartAutoplay() {
    this.stopAutoplay();
    clearTimeout(this.restartTimer);
    this.restartTimer = setTimeout(() => this.startAutoplay(), this.interval);
  }

  getActiveVideo() {
    return this.querySelector(this.mobileQuery.matches ? '.pvs__video--mobile' : '.pvs__video--desktop');
  }

  startVideoAutoplay() {
    if (
      !this.videoAutoplay ||
      !this.isInView ||
      document.hidden ||
      this.reducedMotionQuery.matches
    ) return;

    const media = this.getActiveVideo();
    if (!media || media.classList.contains('pvs__poster-only')) return;

    customElements.whenDefined('deferred-media').then(() => {
      if (!media.isConnected || !this.isInView || document.hidden || this.reducedMotionQuery.matches) return;

      if (media.hasAttribute('data-media-loaded')) media.playMedia?.();
      else media.showDeferredMedia?.();

      const video = media.querySelector('video');
      if (video) {
        video.muted = true;
        video.defaultMuted = true;
        video.play?.().catch(() => {});
      }
    });
  }

  pauseAutoplayVideos() {
    if (!this.videoAutoplay) return;
    this.querySelectorAll('.pvs__video:not(.pvs__poster-only)').forEach((media) => media.pauseMedia?.());
  }

  goToSlide(index, behavior = 'smooth') {
    const slide = this.slides[index];
    if (!this.track || !slide) return;
    this.index = index;
    const firstOffset = this.slides[0]?.offsetLeft || 0;
    this.track.scrollTo({ left: slide.offsetLeft - firstOffset, behavior });
    this.updateDots();
  }

  updateIndex() {
    if (!this.track || this.slides.length === 0) return;
    const firstOffset = this.slides[0].offsetLeft;
    const position = this.track.scrollLeft + firstOffset;
    let closestDistance = Number.POSITIVE_INFINITY;
    this.slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - position);
      if (distance >= closestDistance) return;
      closestDistance = distance;
      this.index = index;
    });
    this.updateDots();
  }

  updateDots() {
    this.slides.forEach((slide, index) => slide.classList.toggle('is-active', index === this.index));
    this.dots.forEach((dot, index) => {
      const active = index === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  setupObserver() {
    if (!('IntersectionObserver' in window)) {
      this.isInView = true;
      this.classList.add('is-revealed');
      this.startAutoplay();
      this.startVideoAutoplay();
      return;
    }

    this.isInView = false;
    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.isInView = entry.isIntersecting;
        if (entry.isIntersecting) {
          this.classList.add('is-revealed');
          this.startAutoplay();
          this.startVideoAutoplay();
        } else {
          this.stopAutoplay();
          this.pauseAutoplayVideos();
        }
      },
      { rootMargin: '140px 0px', threshold: 0.04 }
    );
    this.observer.observe(this);
  }
}

if (!customElements.get('premium-video-story')) {
  customElements.define('premium-video-story', PremiumVideoStory);
}
